/**
 * JWT Utility functions for decoding and extracting token information
 */

interface JWTPayload {
  exp?: number; // Expiration time (seconds since epoch)
  iat?: number; // Issued at time
  userId?: string;
  email?: string;
  role?: string;
  [key: string]: unknown;
}

/**
 * Decode a JWT token without verification
 * Note: This only decodes the payload, it does NOT verify the signature
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    // JWT has three parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Replace URL-safe chars and add padding if needed
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const jsonPayload = atob(base64 + padding);

    return JSON.parse(jsonPayload) as JWTPayload;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

/**
 * Get the expiration timestamp (in milliseconds) from a JWT token
 * Returns null if token is invalid or has no expiration
 */
export const getTokenExpiration = (token: string): number | null => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  // Convert exp (seconds) to milliseconds
  return payload.exp * 1000;
};

/**
 * Check if a token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return true; // Consider invalid tokens as expired
  }

  return Date.now() >= expiration;
};

/**
 * Check if a token will expire within the specified minutes
 * @param token - JWT token to check
 * @param minutes - Number of minutes to check ahead
 * @returns true if token will expire within the specified minutes
 */
export const willTokenExpireSoon = (
  token: string,
  minutes: number
): boolean => {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return true; // Consider invalid tokens as expiring soon
  }

  const expiresIn = expiration - Date.now();
  const minutesInMs = minutes * 60 * 1000;

  return expiresIn <= minutesInMs;
};

/**
 * Get the time remaining until token expiration in milliseconds
 * Returns 0 if token is expired or invalid
 */
export const getTimeUntilExpiration = (token: string): number => {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return 0;
  }

  const remaining = expiration - Date.now();
  return Math.max(0, remaining);
};

/**
 * Get a human-readable time remaining string
 */
export const getReadableTimeRemaining = (token: string): string => {
  const ms = getTimeUntilExpiration(token);
  if (ms === 0) {
    return 'Expired';
  }

  const minutes = Math.floor(ms / (60 * 1000));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''}`;
  }
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  return `${minutes} minute${minutes > 1 ? 's' : ''}`;
};
